import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, CheckCircle, Clock, User, Calendar, Globe, ArrowLeft, Loader, PartyPopper } from 'lucide-react';
import { SignatureCapture } from '../../components/documents/SignatureCapture';
import { DocumentPreview } from '../../components/documents/DocumentPreview';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/calculations';
type DocumentType = 'tenancy_agreement' | 'financing_agreement' | 'payment_authorization';
interface DocumentParty {
  role: string;
  name: string;
  email: string;
  signed: boolean;
  signedAt?: string;
}
interface DocumentData {
  id: string;
  type: DocumentType;
  title: string;
  createdAt: string;
  parties: DocumentParty[];
  content: {
    propertyAddress?: string;
    leaseDuration?: string;
    monthlyRent?: number;
    annualRent?: number;
    securityDeposit?: number;
    moveInDate?: string;
    totalAmount?: number;
    monthlyPayment?: number;
    paymentSchedule?: {
      month: number;
      dueDate: string;
      amount: number;
    }[];
    bankName?: string;
    accountNumber?: string;
  };
}
// Mock documents
const MOCK_DOCUMENTS: Record<string, DocumentData> = {
  'doc-tenancy-001': {
    id: 'doc-tenancy-001',
    type: 'tenancy_agreement',
    title: 'Tenancy Agreement',
    createdAt: new Date().toISOString(),
    parties: [{
      role: 'Tenant',
      name: 'Adebayo Johnson',
      email: 'adebayo@example.com',
      signed: false
    }, {
      role: 'Landlord',
      name: 'Tunde Bakare',
      email: 'tunde@example.com',
      signed: true,
      signedAt: new Date(Date.now() - 86400000).toISOString()
    }],
    content: {
      propertyAddress: '12 Admiralty Way, Lekki Phase 1, Lagos',
      leaseDuration: '12 months',
      annualRent: 2400000,
      securityDeposit: 200000,
      moveInDate: new Date(Date.now() + 7 * 86400000).toISOString()
    }
  },
  'doc-financing-001': {
    id: 'doc-financing-001',
    type: 'financing_agreement',
    title: 'Rent Financing Agreement',
    createdAt: new Date().toISOString(),
    parties: [{
      role: 'Tenant',
      name: 'Adebayo Johnson',
      email: 'adebayo@example.com',
      signed: false
    }, {
      role: 'Renta',
      name: 'Renta Technologies Limited',
      email: 'legal@renta.ng',
      signed: true,
      signedAt: new Date().toISOString()
    }],
    content: {
      totalAmount: 3000000,
      monthlyPayment: 250000,
      paymentSchedule: Array.from({
        length: 12
      }, (_, i) => ({
        month: i + 1,
        dueDate: new Date(Date.now() + (i + 1) * 30 * 86400000).toISOString(),
        amount: 250000
      }))
    }
  },
  'doc-payment-001': {
    id: 'doc-payment-001',
    type: 'payment_authorization',
    title: 'Payment Authorization',
    createdAt: new Date().toISOString(),
    parties: [{
      role: 'Tenant',
      name: 'Adebayo Johnson',
      email: 'adebayo@example.com',
      signed: false
    }],
    content: {
      monthlyPayment: 250000,
      bankName: 'First Bank of Nigeria',
      accountNumber: '****1234'
    }
  }
};
const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  tenancy_agreement: 'Tenancy Agreement',
  financing_agreement: 'Financing Agreement',
  payment_authorization: 'Payment Authorization'
};
const DOCUMENT_TYPE_COLORS: Record<DocumentType, string> = {
  tenancy_agreement: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  financing_agreement: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  payment_authorization: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
};
export function DocumentSigning() {
  const {
    documentId
  } = useParams<{
    documentId: string;
  }>();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [signature, setSignature] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [ipAddress, setIpAddress] = useState('192.168.1.1');
  // Load document
  useEffect(() => {
    const loadDocument = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      const doc = MOCK_DOCUMENTS[documentId || ''];
      if (doc) {
        setDocument(doc);
        // Pre-fill name for current user
        const currentUser = doc.parties.find(p => !p.signed);
        if (currentUser) {
          setFullName(currentUser.name);
        }
      }
      setIsLoading(false);
    };
    loadDocument();
  }, [documentId]);
  const currentSigner = document?.parties.find(p => !p.signed);
  const canSign = signature && fullName.trim() === currentSigner?.name && consent;
  const alreadySigned = document?.parties.every(p => p.signed);
  const handleSign = async () => {
    if (!canSign || !document || !currentSigner) return;
    setIsSigning(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    // Update document state
    setDocument({
      ...document,
      parties: document.parties.map(p => p.role === currentSigner.role ? {
        ...p,
        signed: true,
        signedAt: new Date().toISOString()
      } : p)
    });
    // Save signature to localStorage (simulated)
    localStorage.setItem(`signature-${documentId}`, JSON.stringify({
      signature,
      name: fullName,
      timestamp: new Date().toISOString(),
      ip: ipAddress
    }));
    setIsSigning(false);
    setIsComplete(true);
    toast.success('Document signed successfully!');
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  if (isLoading) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center" data-id="element-4415">
        <div className="text-center" data-id="element-4416">
          <Loader className="h-12 w-12 text-gray-400 animate-spin mx-auto mb-4" data-id="element-4417" />
          <p className="text-gray-600 dark:text-gray-400" data-id="element-4418">
            Loading document...
          </p>
        </div>
      </div>;
  }
  if (!document) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center" data-id="element-4419">
        <div className="text-center" data-id="element-4420">
          <FileText className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" data-id="element-4421" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4422">
            Document Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4" data-id="element-4423">
            The document you're looking for doesn't exist or has been removed.
          </p>
          <button onClick={() => navigate('/tenant/dashboard')} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-4424">
            Return to Dashboard
          </button>
        </div>
      </div>;
  }
  // Success state
  if (isComplete) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4" data-id="element-4425">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-id="element-4426">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          delay: 0.2
        }} className="mb-6" data-id="element-4427">
            <div className="relative inline-block" data-id="element-4428">
              <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto" data-id="element-4429">
                <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" data-id="element-4430" />
              </div>
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="absolute -top-2 -right-2" data-id="element-4431">
                <PartyPopper className="h-8 w-8 text-yellow-500" data-id="element-4432" />
              </motion.div>
            </div>
          </motion.div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4433">
            Document Signed!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-4434">
            Your signature has been recorded. A copy of the signed document has
            been sent to your email.
          </p>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-left" data-id="element-4435">
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4436">
              <strong data-id="element-4437">Document:</strong> {document.title}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4438">
              <strong data-id="element-4439">Signed by:</strong> {currentSigner?.name}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4440">
              <strong data-id="element-4441">Date:</strong> {formatDate(new Date().toISOString())}
            </p>
          </div>

          <div className="space-y-3" data-id="element-4442">
            <button onClick={() => navigate('/tenant/documents')} className="w-full py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors" data-id="element-4443">
              View All Documents
            </button>
            <button onClick={() => navigate('/tenant/dashboard')} className="w-full py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-4444">
              Return to Dashboard
            </button>
          </div>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-id="element-4445">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-4446">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-4447">
          <div className="flex items-center justify-between" data-id="element-4448">
            <div className="flex items-center space-x-4" data-id="element-4449">
              <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-4450">
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" data-id="element-4451" />
              </button>
              <div className="flex items-center space-x-3" data-id="element-4452">
                <div className="h-10 w-10 bg-gray-900 dark:bg-white rounded-lg flex items-center justify-center" data-id="element-4453">
                  <span className="text-white dark:text-gray-900 font-bold text-lg" data-id="element-4454">
                    R
                  </span>
                </div>
                <div data-id="element-4455">
                  <h1 className="font-bold text-gray-900 dark:text-white" data-id="element-4456">
                    {document.title}
                  </h1>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${DOCUMENT_TYPE_COLORS[document.type]}`} data-id="element-4457">
                    {DOCUMENT_TYPE_LABELS[document.type]}
                  </span>
                </div>
              </div>
            </div>

            {/* Signing Progress */}
            <div className="hidden sm:flex items-center space-x-4" data-id="element-4458">
              {document.parties.map((party, index) => <div key={party.role} className="flex items-center" data-id="element-4459">
                  {index > 0 && <div className="w-8 h-px bg-gray-300 dark:bg-gray-600 mx-2" data-id="element-4460" />}
                  <div className="flex items-center space-x-2" data-id="element-4461">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${party.signed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-4462">
                      {party.signed ? <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" data-id="element-4463" /> : <Clock className="h-4 w-4 text-gray-400" data-id="element-4464" />}
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4465">
                      {party.role}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4466">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-id="element-4467">
          {/* Document Preview */}
          <div className="lg:col-span-3 h-[calc(100vh-200px)]" data-id="element-4468">
            <DocumentPreview document={document} zoom={zoom} onZoomChange={setZoom} data-id="element-4469" />
          </div>

          {/* Signing Panel */}
          <div className="lg:col-span-2" data-id="element-4470">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-8" data-id="element-4471">
              {alreadySigned ? <div className="text-center py-8" data-id="element-4472">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" data-id="element-4473" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4474">
                    Fully Signed
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-4475">
                    All parties have signed this document.
                  </p>
                </div> : <>
                  {/* Document Info */}
                  <div className="mb-6" data-id="element-4476">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-4" data-id="element-4477">
                      Document Details
                    </h3>
                    <div className="space-y-3 text-sm" data-id="element-4478">
                      <div className="flex items-center space-x-3" data-id="element-4479">
                        <FileText className="h-4 w-4 text-gray-400" data-id="element-4480" />
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-4481">
                          {DOCUMENT_TYPE_LABELS[document.type]}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3" data-id="element-4482">
                        <Calendar className="h-4 w-4 text-gray-400" data-id="element-4483" />
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-4484">
                          Created {formatDate(document.createdAt)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3" data-id="element-4485">
                        <User className="h-4 w-4 text-gray-400" data-id="element-4486" />
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-4487">
                          Signing as: {currentSigner?.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3" data-id="element-4488">
                        <Globe className="h-4 w-4 text-gray-400" data-id="element-4489" />
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-4490">
                          IP: {ipAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Signature Section */}
                  <div className="mb-6" data-id="element-4491">
                    <SignatureCapture onSignatureChange={setSignature} disabled={isSigning} data-id="element-4492" />
                  </div>

                  {/* Full Name Verification */}
                  <div className="mb-6" data-id="element-4493">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-4494">
                      Type your full name to verify
                    </label>
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} disabled={isSigning} placeholder={currentSigner?.name} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50" data-id="element-4495" />
                    {fullName && fullName !== currentSigner?.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-4496">
                        Name must match: {currentSigner?.name}
                      </p>}
                  </div>

                  {/* Consent Checkbox */}
                  <div className="mb-6" data-id="element-4497">
                    <label className="flex items-start space-x-3 cursor-pointer" data-id="element-4498">
                      <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} disabled={isSigning} className="mt-1 h-4 w-4 text-gray-900 dark:text-white border-gray-300 rounded focus:ring-gray-900 dark:focus:ring-white" data-id="element-4499" />
                      <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4500">
                        I agree to sign this document electronically and
                        understand that my electronic signature is legally
                        binding.
                      </span>
                    </label>
                  </div>

                  {/* Sign Button */}
                  <button onClick={handleSign} disabled={!canSign || isSigning} className="w-full py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-4501">
                    {isSigning ? <>
                        <Loader className="h-5 w-5 animate-spin mr-2" data-id="element-4502" />
                        Signing Document...
                      </> : 'Sign Document'}
                  </button>

                  {/* Signing Progress */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-4503">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-4504">
                      Signing Progress
                    </h4>
                    <div className="space-y-2" data-id="element-4505">
                      {document.parties.map(party => <div key={party.role} className="flex items-center justify-between text-sm" data-id="element-4506">
                          <div className="flex items-center space-x-2" data-id="element-4507">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${party.signed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-4508">
                              {party.signed ? <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-400" data-id="element-4509" /> : <Clock className="h-3 w-3 text-gray-400" data-id="element-4510" />}
                            </div>
                            <span className="text-gray-700 dark:text-gray-300" data-id="element-4511">
                              {party.name}
                            </span>
                          </div>
                          <span className={`text-xs ${party.signed ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} data-id="element-4512">
                            {party.signed ? 'Signed' : 'Pending'}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </>}
            </motion.div>
          </div>
        </div>
      </main>
    </div>;
}